<template>
  <div class="table-wrapper" :class="{ dragging }" @mouseup="endDrag">
    <div class="table-scroll" ref="scroll">
      <table>
        <thead>
          <tr @contextmenu.prevent="$emit('contextmenu:header', $event)">
            <th v-if="enableSelection" class="first table-select shrink">
              <ux-menu>
                <template #activator="{ on }">
                  <ux-checkbox v-on="on" :value="pageSelected"></ux-checkbox>
                </template>
                <ux-list>
                  <ux-list-item @click="selectPage()">
                    Select Page
                  </ux-list-item>
                  <ux-list-item
                    v-if="someSelectedOnPage"
                    @click="deselectPage()"
                  >
                    Deselect Page
                  </ux-list-item>
                  <ux-list-item v-if="selectAll" @click="selectAll()">
                    Select All ({{ total }})
                  </ux-list-item>
                  <ux-list-item v-if="deselectAll" @click="deselectAll()">
                    Deselect All
                  </ux-list-item>
                </ux-list>
              </ux-menu>
            </th>
            <th
              @click="toggleSort(column)"
              :class="[
                {
                  sortable: column.sortable !== false,
                  active: column.key === sorting?.key,
                },
                column.class,
              ]"
              v-for="column in renderColumns"
            >
              <flex-row gap vcenter
                ><flex-cell>{{ column.title }}</flex-cell
                ><flex-cell
                  class="caret"
                  v-if="column.key === sorting?.key"
                  shrink
                  >{{ currentSortDirection === "asc" ? "▲" : "▼" }}</flex-cell
                ></flex-row
              >
            </th>
            <th v-if="enableActions || $slots.corner" class="last shrink">
              <slot name="corner"></slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groupedRows">
            <tr v-if="group.title">
              <td>{{ group.title }}</td>
            </tr>
            <table-row
              :enableSelection="enableSelection"
              :enableActions="enableActions"
              :hasCorner="!!$slots.corner"
              :key="row._id"
              @click:cell="clickCell"
              @click:row="clickRow"
              @mousedown:select="mousedownSelect"
              @mouseenter:select="mouseenterSelect"
              @click:actions="clickActions"
              :selected="isSelected(row)"
              :row="row"
              :index="index"
              :columns="columns"
              v-for="(row, index) in group.rows"
            />
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import TableRow from "./TableRow.vue";
import TableCell from "./TableCell.vue";
import RememberScrollMixin from "../mixins/RememberScroll.js";
import _get from "lodash/get";
export default {
  mixins: [RememberScrollMixin],
  components: {
    TableRow,
    TableCell,
  },
  emits: [
    "update:sort",
    "select:multiple",
    "deselect:multiple",
    "select:multiple",
    "click:row",
    "click:cell",
    "click:actions",
    "select:row:toggle",
    "contextmenu:header",
  ],
  watch: {
    rows() {
      this.$refs.scroll.scroll({
        top: 0,
        left: 0,
      });
    },
    sort(s) {
      this.sorting = s;
    },
    sorting(s) {
      this.$emit("update:sort", s);
    },
  },
  props: {
    total: {
      type: Number,
    },
    columns: {
      type: Array,
      default() {
        return [];
      },
    },
    rows: {
      type: Array,
      default() {
        return [];
      },
    },
    scrollable: {
      type: Boolean,
      default() {
        return true;
      },
    },
    enableActions: {
      type: Boolean,
      default() {
        return false;
      },
    },
    enableSelection: {
      type: Boolean,
      default() {
        return true;
      },
    },
    sort: {
      type: Object,
      default() {
        return {
          key: "meta.created",
          type: "date",
          direction: "desc",
        };
      },
    },
    selection: {
      type: Array,
      default() {
        return [];
      },
    },
    selectAll: {
      type: Function,
    },
    deselectAll: {
      type: Function,
    },
    group: {
      type: Object,
      default() {
        return {
          // key:'meta.created',
          // type:'date',
        };
      },
    },
  },
  data() {
    return {
      sorting: this.sort,
      lastSelectedIndex: null,
      lastSelectAction: null,
      dragging: false,
      dragMode: null,
      dragStart: null,
    };
  },

  beforeUnmount() {
    window.removeEventListener("mouseup", this.endDrag);
    window.removeEventListener("mousemove", this.checkDragStart);
  },
  computed: {
    groupedRows() {
      const groupingKey = this.group.key;
      const groupingType = this.group.type || "string";

      if (groupingKey) {
        const { values } = this.renderRows.reduce(
          function (memo, row) {
            let groupingValue = _get(row, groupingKey);

            switch (groupingType) {
              default:
                groupingValue = String(groupingValue);
                break;
            }
            let match = memo.keys[groupingValue];
            if (!match) {
              match = memo.keys[groupingValue] = {
                title: groupingValue,
                rows: [],
              };

              memo.values.push(match);
            }

            return memo;
          },
          {
            values: [],
            keys: {},
          },
        );

        return values;
      }

      return [{ rows: this.renderRows }];
    },
    currentSortDirection() {
      return this.sorting?.direction || "asc";
    },
    selectionHash() {
      var self = this;
      return self.selection.reduce(function (set, row) {
        var id = row._id || row.id;
        set[id] = row;
        return set;
      }, {});
    },
    pageSelected() {
      var self = this;

      if (!self.rows || !self.rows.length) {
        return;
      }

      //Check to see if any rows are not selected
      return !self.rows.some(function (row) {
        return !self.selectionHash[row._id || row.id];
      });
    },
    renderColumns() {
      const self = this;
      return self.columns.map(function (column) {
        var col = Object.assign({}, column);

        col.class = [];
        if (col.shrink) {
          col.class.push("shrink");
        }

        return col;
      });
    },
    renderRows() {
      return this.rows;
    },
    someSelectedOnPage() {
      return this.rows.some(this.isSelected);
    },
  },
  methods: {
    togglePage() {
      var self = this;

      if (self.pageSelected) {
        self.deselectPage();
      } else {
        self.selectPage();
      }
    },
    selectPage() {
      var self = this;
      self.$emit("select:multiple", self.rows);
    },
    deselectPage() {
      var self = this;
      self.$emit("deselect:multiple", self.rows);
    },

    isSelected(row) {
      var self = this;
      var rowID = row._id || row.id;
      return self.selection.some(function (r) {
        var rid = r._id || r.id;
        return r == row || rid == rowID;
      });
    },
    classes(row) {
      let array = [];
      if (this.isSelected(row)) {
        array.push("selected");
      }
      return array;
    },
    selectRange(start, end) {
      const startIndex = Math.min(start, end);
      const endIndex = Math.max(start, end) + 1;
      const targetRows = this.renderRows.slice(startIndex, endIndex);
      this.$emit("select:multiple", targetRows);
    },
    deselectRange(start, end) {
      const startIndex = Math.min(start, end);
      const endIndex = Math.max(start, end) + 1;
      const targetRows = this.renderRows.slice(startIndex, endIndex);
      this.$emit("deselect:multiple", targetRows);
    },
    toggleSort(column) {
      const currentKey = this.sorting?.key;
      const currentDirection = this.sorting?.direction || "asc";

      let { key, direction } = column;

      if (key === currentKey) {
        direction = currentDirection === "dsc" ? "asc" : "dsc";
      }

      this.sorting = {
        key,
        direction,
        type: column.type,
      };
    },
    clickRow(row) {
      this.$emit("click:row", row);
    },
    clickCell({ row, column }) {
      this.$emit("click:cell", { row, column });
    },
    clickActions(row) {
      this.$emit("click:actions", row);
    },
    mousedownSelect(row, index, event) {
      const wasSelected = this.isSelected(row);
      const action = wasSelected ? "deselect" : "select";

      if (
        event.shiftKey &&
        this.lastSelectedIndex != null &&
        this.lastSelectedIndex !== index
      ) {
        if (this.lastSelectAction === "deselect") {
          this.deselectRange(this.lastSelectedIndex, index);
        } else {
          this.selectRange(this.lastSelectedIndex, index);
        }
        this.lastSelectedIndex = index;
        return;
      }

      if (action === "select") {
        this.$emit("select:multiple", [row]);
      } else {
        this.$emit("deselect:multiple", [row]);
      }

      this.lastSelectedIndex = index;
      this.lastSelectAction = action;

      this.dragMode = action;
      this.dragStart = { x: event.clientX, y: event.clientY };
      window.addEventListener("mousemove", this.checkDragStart);
      window.addEventListener("mouseup", this.endDrag, { once: true });
    },
    mouseenterSelect(row, index) {
      if (!this.dragging) {
        return;
      }
      const isCurrentlySelected = this.isSelected(row);
      if (this.dragMode === "select" && !isCurrentlySelected) {
        this.$emit("select:multiple", [row]);
      } else if (this.dragMode === "deselect" && isCurrentlySelected) {
        this.$emit("deselect:multiple", [row]);
      }
    },
    checkDragStart(event) {
      if (!this.dragStart) return;
      const dx = event.clientX - this.dragStart.x;
      const dy = event.clientY - this.dragStart.y;
      if (dx * dx + dy * dy >= 9) {
        this.dragging = true;
        window.removeEventListener("mousemove", this.checkDragStart);
      }
    },
    endDrag() {
      this.dragging = false;
      this.dragMode = null;
      this.dragStart = null;
      window.removeEventListener("mouseup", this.endDrag);
      window.removeEventListener("mousemove", this.checkDragStart);
    },
  },
};
</script>
<style scoped lang="scss">
$light: var(--bg1, #fff);
$dark: color-mix(in lab, currentColor 1%, var(--bg1));
$border-color: color-mix(in lab, currentColor 3.5%, transparent);
.table-wrapper {
  &.dragging {
    user-select: none;
  }
  // box-sizing:border-box;
  //
  background: $light;
  display: flex;
  flex: 1;
  overflow: hidden;

  .table-scroll {
    overflow: auto;
    position: relative;
    overscroll-behavior: none;
    width: 100%;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;

    thead {
      th {
        top: 0;
        position: sticky;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        white-space: nowrap;
        font-size: 0.6em;
        padding: 0.5em;
        z-index: 2;
        text-align: left;
        border-bottom: 1px solid $border-color;

        background: $light;

        .caret {
          opacity: 0.5;
        }

        &:before {
          content: "";
          display: none;
          background: $border-color;
          position: absolute;
          width: 100%;
          height: 100%;
          left: 0;
          top: 0;
        }

        &.table-select {
          z-index: 3;
          font-size: 1em;
        }

        &.sortable {
          cursor: pointer;

          &:hover {
            &:before {
              display: block;
            }
          }
        }

        &.active {
          &:before {
            display: block;
          }
        }
      }
    }

    th.first,
    td.first {
      position: sticky;
      left: 0;
      // z-index: 1;
      text-align: center;
    }

    th.last,
    td.last {
      position: sticky;
      right: 0;
      // z-index: 1;
      text-align: center;
    }

    td,
    th {
      padding: 0.6em;
      line-height: 1;

      &.shrink {
        width: 1px;
      }
    }

    tr {
      border-bottom: 1px solid $border-color;
      &:last-of-type {
        border-bottom: none;
      }
    }

    tr:nth-child(even) {
      background: $dark;

      th.first,
      td.first,
      th.last,
      td.last {
        background: $dark;
      }
    }

    tr:nth-child(odd) {
      th.first,
      td.first,
      th.last,
      td.last {
        background: $light;
      }
    }

    tbody {
      tr:hover {
        &,
        th.first,
        td.first,
        th.last,
        td.last {
          background: color-mix(in lab, var(--primary) 5%, var(--bg1));
          color: color-mix(in lab, var(--primary) 50%, currentColor);
          border-color: color-mix(in lab, var(--primary) 5%, var(--bg1));

          // color: color-mix(in lab, #328c8c 70%, var(--bg1));
          // background: color-mix(in lab, #ebfbfb 70%, var(--bg1));
        }
      }
      tr.selected {
        &,
        th.first,
        td.first,
        th.last,
        td.last {
          background: color-mix(in lab, var(--primary) 15%, var(--bg1));
          color: color-mix(in lab, var(--primary) 80%, currentColor);
          border-color: color-mix(in lab, var(--primary) 15%, var(--bg1));
        }

        &:hover {
          &,
          th.first,
          td.first,
          th.last,
          td.last {
            background: color-mix(in lab, var(--primary) 12%, var(--bg1));
            color: color-mix(in lab, var(--primary) 50%, currentColor);
            border-color: color-mix(in lab, var(--primary) 12%, var(--bg1));
          }
        }
      }
    }
  }
}
</style>
